import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  FaStar, FaRegStar,
  FaGraduationCap, FaCalendarAlt, FaMapMarkerAlt,
  FaChevronRight, FaChevronLeft, FaSpinner,
  FaExclamationTriangle, FaAward, FaUsers, FaChartBar,
  FaThumbsUp, FaThumbsDown, FaMinus
} from 'react-icons/fa';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer
} from 'recharts';
import { evalApi } from '../hooks/useEvalApi';

//  Traductions FR / EN ─
const T = {
  fr: {
    welcome: 'Bienvenue', welcomeSub: 'Votre avis compte. Merci de prendre quelques minutes pour évaluer cette formation.',
    start: "Commencer l'évaluation",
    stepAvis: 'Évaluation', stepRecap: 'Résumé', stepDone: 'Terminé',
    avisTitle: 'Évaluation à chaud',
    avisRatingHint: 'Notez chaque critère de 1 (insatisfaisant) à 5 (excellent).',
    avisSection1: 'La Formation', avisSection2: 'Le Formateur',
    avisSection3: "L'Impact", avisSection4: 'La Logistique',
    criteres: {
      contenuObjectifs: 'Contenu conforme aux objectifs', theoriePratique: 'Rapport théorie / pratique',
      duree: 'Durée de la formation', rythme: 'Rythme de la formation',
      support: 'Support pédagogique', adequation: 'Adéquation avec les objectifs initiaux',
      apport: 'Apport immédiat pour votre poste', transfert: 'Transférabilité à votre contexte',
      clarte: 'Clarté du cours', maitrise: 'Maîtrise du sujet',
      disponibilite: 'Disponibilité du formateur', methode: 'Méthode pédagogique',
      utilite: 'Utilité en situation de travail', developpement: 'Développement professionnel',
      salle: 'Salle de formation', materiel: 'Matériel et projection',
      restauration: 'Qualité de la restauration',
    },
    recommandationLabel: 'Recommanderiez-vous cette formation ?',
    recommandationOpts: ['Oui, sans hésitation', 'Oui, avec réserves', 'Non'],
    pointsForts: 'Points forts de la formation',
    pointsFaibles: 'Points à améliorer',
    commentairePlaceholder: 'Commentaires généraux (facultatif)…',
    pointsFortsPlaceholder: "Qu'avez-vous particulièrement apprécié ? (facultatif)",
    pointsFaiblesPlaceholder: "Qu'est-ce qui pourrait être amélioré ? (facultatif)",
    commentaireLabel: 'Commentaire général',
    next: 'Suivant', prev: 'Retour', submit: 'Envoyer mon évaluation', submitting: 'Envoi en cours…',
    rateAll: 'Veuillez noter tous les critères avant de continuer.',
    recapTitle: 'Votre résumé',
    recapSub: 'Voici un aperçu de votre évaluation.',
    recapGlobal: 'Note globale',
    recapRadar: 'Vue par catégorie',
    recapContinue: 'Valider et terminer',
    doneTitle: 'Merci pour votre participation !',
    doneSub: "Vos réponses ont bien été enregistrées. Bonne suite de formation !",
    doneSign: "L'équipe CINEF Formation",
    loadingFormation: 'Chargement de la formation…',
    tokenInvalid: 'Ce lien est invalide ou expiré. Veuillez contacter votre formateur.',
    quotaReached: 'Le nombre maximum de réponses a été atteint pour cette formation.',
    submitError: "Une erreur est survenue lors de l'envoi. Veuillez réessayer.",
  },
  en: {
    welcome: 'Welcome', welcomeSub: 'Your feedback matters. Please take a few minutes to evaluate this training.',
    start: 'Start evaluation',
    stepAvis: 'Feedback', stepRecap: 'Summary', stepDone: 'Done',
    avisTitle: 'Training Evaluation',
    avisRatingHint: 'Rate each criterion from 1 (unsatisfactory) to 5 (excellent).',
    avisSection1: 'The Training', avisSection2: 'The Trainer',
    avisSection3: 'The Impact', avisSection4: 'Logistics',
    criteres: {
      contenuObjectifs: 'Content aligned with objectives', theoriePratique: 'Theory / practice balance',
      duree: 'Training duration', rythme: 'Training pace',
      support: 'Learning materials', adequation: 'Alignment with initial objectives',
      apport: 'Immediate contribution to your role', transfert: 'Transferability to your context',
      clarte: 'Clarity of delivery', maitrise: 'Trainer expertise',
      disponibilite: 'Trainer availability', methode: 'Teaching method',
      utilite: 'Usefulness in work situations', developpement: 'Professional development',
      salle: 'Training room', materiel: 'Equipment and projection',
      restauration: 'Catering quality',
    },
    recommandationLabel: 'Would you recommend this training?',
    recommandationOpts: ['Yes, definitely', 'Yes, with reservations', 'No'],
    pointsForts: 'Strengths of the training',
    pointsFaibles: 'Areas for improvement',
    commentairePlaceholder: 'General comments (optional)…',
    pointsFortsPlaceholder: 'What did you particularly appreciate? (optional)',
    pointsFaiblesPlaceholder: 'What could be improved? (optional)',
    commentaireLabel: 'General comment',
    next: 'Next', prev: 'Back', submit: 'Submit my evaluation', submitting: 'Submitting…',
    rateAll: 'Please rate all criteria before continuing.',
    recapTitle: 'Your summary',
    recapSub: 'Here is an overview of your evaluation.',
    recapGlobal: 'Overall score',
    recapRadar: 'Overview by category',
    recapContinue: 'Confirm and finish',
    doneTitle: 'Thank you for your participation!',
    doneSub: 'Your responses have been recorded. Enjoy the rest of your training!',
    doneSign: 'The CINEF Training Team',
    loadingFormation: 'Loading training data…',
    tokenInvalid: 'This link is invalid or expired. Please contact your trainer.',
    quotaReached: 'The maximum number of responses has been reached for this training.',
    submitError: 'An error occurred while submitting. Please try again.',
  }
};

//  Sections avis ─
const SECTIONS_AVIS = [
  { key: 'formation',  sectionKey: 'avisSection1', color: '#198754', criteres: ['contenuObjectifs','theoriePratique','duree','rythme','support','adequation','apport','transfert'] },
  { key: 'formateur',  sectionKey: 'avisSection2', color: '#fd7e14', criteres: ['clarte','maitrise','disponibilite','methode'] },
  { key: 'impact',     sectionKey: 'avisSection3', color: '#20c997', criteres: ['utilite','developpement'] },
  { key: 'logistique', sectionKey: 'avisSection4', color: '#0d6efd', criteres: ['salle','materiel','restauration'] },
];

const initNotes = () => {
  const n = {};
  SECTIONS_AVIS.forEach(s => s.criteres.forEach(c => { n[`${s.key}.${c}`] = 0; }));
  return n;
};

const avg = arr => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
const noteColor = n => {
  if (n >= 4.5) return '#198754';
  if (n >= 3.5) return '#20c997';
  if (n >= 2.5) return '#fd7e14';
  return '#dc3545';
};
const noteLabel = (n, lang) => {
  if (n >= 4.5) return lang === 'fr' ? 'Excellent' : 'Excellent';
  if (n >= 3.5) return lang === 'fr' ? 'Bien' : 'Good';
  if (n >= 2.5) return lang === 'fr' ? 'Moyen' : 'Average';
  return lang === 'fr' ? 'Insuffisant' : 'Insufficient';
};

//  COMPOSANT RÉCAPITULATIF ─
const RecapitulatifAvis = ({ notes, t, lang, onContinue, submitting }) => {
  const catMoyennes = useMemo(() => {
    return SECTIONS_AVIS.map(s => {
      const vals = s.criteres.map(c => notes[`${s.key}.${c}`]).filter(v => v > 0);
      return { key: s.key, label: t[s.sectionKey], color: s.color, moy: avg(vals) };
    });
  }, [notes, t]);

  const noteGlobale = useMemo(() => avg(catMoyennes.map(c => c.moy)), [catMoyennes]);

  const radarData = catMoyennes.map(c => ({
    sujet: c.label.replace("L'", '').replace('La ', '').replace('Le ', ''),
    note: +c.moy.toFixed(2),
    fullMark: 5,
  }));

  return (
    <div className="ep-card ep-recap-card">
      <div className="ep-recap-header">
        <div className="ep-recap-icon"><FaChartBar /></div>
        <h2 className="ep-step-title">{t.recapTitle}</h2>
        <p className="ep-step-sub">{t.recapSub}</p>
      </div>
      <div className="ep-recap-global">
        <div className="ep-score-ring" style={{ '--score-deg': `${(noteGlobale / 5) * 360}deg`, '--score-color': noteColor(noteGlobale) }}>
          <div className="ep-score-inner">
            <span className="ep-score-num">{noteGlobale.toFixed(1)}</span>
            <span className="ep-score-den">/5</span>
          </div>
        </div>
        <div className="ep-score-info">
          <div className="ep-score-label" style={{ color: noteColor(noteGlobale) }}>{noteLabel(noteGlobale, lang)}</div>
          <div className="ep-score-sub">{t.recapGlobal}</div>
          <div className="ep-stars-row">
            {[1,2,3,4,5].map(s => (
              <span key={s} style={{ color: noteGlobale >= s ? '#fd7e14' : '#e2e8f0', fontSize: 18 }}>
                {noteGlobale >= s ? <FaStar /> : <FaRegStar />}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="ep-recap-cats">
        {catMoyennes.map(cat => (
          <div key={cat.key} className="ep-recap-cat">
            <div className="ep-recap-cat-label">{cat.label}</div>
            <div className="ep-recap-cat-bar-wrap">
              <div className="ep-recap-cat-bar" style={{ width: `${(cat.moy / 5) * 100}%`, background: cat.color }} />
            </div>
            <div className="ep-recap-cat-score" style={{ color: noteColor(cat.moy) }}>{cat.moy.toFixed(1)}</div>
          </div>
        ))}
      </div>
      <div className="ep-recap-section-title">{t.recapRadar}</div>
      <div style={{ height: 220, marginBottom: 24 }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={radarData}>
            <PolarGrid stroke="rgba(0,0,0,0.08)" />
            <PolarAngleAxis dataKey="sujet" tick={{ fontSize: 12, fill: '#718096', fontFamily: "'DM Sans',sans-serif" }} />
            <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fontSize: 10, fill: '#b0bec5' }} tickCount={6} />
            <Radar name="Note" dataKey="note" stroke="#198754" fill="#198754" fillOpacity={0.15} strokeWidth={2.5} dot={{ fill: '#198754', r: 4 }} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      <button className="ep-btn ep-btn-primary" onClick={onContinue} disabled={submitting} style={{ marginTop: 8 }}>
        {submitting
          ? <><FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> {t.submitting}</>
          : <>{t.recapContinue} <FaChevronRight /></>
        }
      </button>
    </div>
  );
};

//  COMPOSANT PRINCIPAL ─
const EvalPublic = () => {
  const { token } = useParams();

  const [lang,           setLang]           = useState('fr');
  const [step,           setStep]           = useState(0);
  const [formation,      setFormation]      = useState(null);
  const [sessionId,      setSessionId]      = useState(null);
  const [tokenValid,     setTokenValid]     = useState(null);
  const [tokenError,     setTokenError]     = useState('');
  const [notes,          setNotes]          = useState(initNotes());
  const [recommandation, setRecommandation] = useState(null);
  const [pointsForts,    setPointsForts]    = useState('');
  const [pointsFaibles,  setPointsFaibles]  = useState('');
  const [commentaire,    setCommentaire]    = useState('');
  const [avisErr,        setAvisErr]        = useState('');
  const [submitErr,      setSubmitErr]      = useState('');
  const [submitting,     setSubmitting]     = useState(false);

  const t = T[lang];

  //  Chargement de la session via token 
  useEffect(() => {
    setTokenValid(null);
    setTokenError('');

    evalApi.getPublicSession(token)
      .then(res => {
        const data = res.data;
        if (data.valid) {
          setFormation({
            titre: data.titre,
            dates: data.dates,
            lieu: data.lieu,
            formateurs: data.formateurs,
            participantsAttendus: data.participants_attendus,
            participantsRepondus: data.participants_repondus,
          });
          setSessionId(data.session_id);
          setTokenValid(true);
        } else {
          setTokenError(data.message || t.tokenInvalid);
          setTokenValid(false);
        }
      })
      .catch(err => {
        const msg = err.response?.data?.message || t.tokenInvalid;
        setTokenError(msg);
        setTokenValid(false);
      });
  }, [token]);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, [step]);

  //  Validation de l'étape avis → récap ─
  const handleSubmitAvis = () => {
    const unrated = Object.values(notes).some(v => v === 0);
    if (unrated) { setAvisErr(t.rateAll); return; }
    setAvisErr('');
    setStep(2);
  };

  //  Soumission finale vers l'API 
  const handleFinalSubmit = async () => {
    setSubmitting(true);
    setSubmitErr('');

    // Construire les objets de notes par section
    const buildSection = (sectionKey) => {
      const section = SECTIONS_AVIS.find(s => s.key === sectionKey);
      const obj = {};
      section.criteres.forEach(c => { obj[c] = notes[`${sectionKey}.${c}`]; });
      return obj;
    };

    const payload = {
      notes_formation:  buildSection('formation'),
      notes_formateur:  buildSection('formateur'),
      notes_impact:     buildSection('impact'),
      notes_logistique: buildSection('logistique'),
      recommandation,
      points_forts:   pointsForts  || null,
      points_faibles: pointsFaibles || null,
      commentaire:    commentaire  || null,
    };

    try {
      await evalApi.soumettre(token, payload);
      setStep(3);
    } catch (err) {
      const msg = err.response?.data?.message || t.submitError;
      setSubmitErr(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const setNote = (key, val) => setNotes(n => ({ ...n, [key]: val }));

  const progressMap = { 0: 0, 1: 30, 2: 65, 3: 100 };
  const progress = progressMap[step] ?? 0;
  const stepDots = [
    { id: 1, label: t.stepAvis },
    { id: 2, label: t.stepRecap },
    { id: 3, label: t.stepDone },
  ];

  return (
    <>
      <div className="ep-wrap">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&family=DM+Serif+Display:ital@0;1&display=swap');
          *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
          .ep-wrap { font-family:'DM Sans',-apple-system,sans-serif; min-height:100vh; padding-bottom:72px; padding-top: env(safe-area-inset-top, 0px); }
          .ep-lang { position:fixed; top:16px; right:16px; z-index:999; display:flex; background:#fff; border:1.5px solid #e2e8f0; border-radius:10px; overflow:hidden; box-shadow:0 4px 16px rgba(0,0,0,0.08); }
          .ep-lang-btn { padding:8px 14px; border:none; background:transparent; font-family:'DM Sans',sans-serif; font-size:13px; font-weight:700; color:#718096; cursor:pointer; transition:all 0.18s; }
          .ep-lang-btn.on { background:#fd7e14; color:#fff; }
          .ep-prog-wrap { position:sticky; top:0; z-index:100; background:rgba(255,255,255,0.95); backdrop-filter:blur(12px); border-bottom:1px solid rgba(0,0,0,0.06); padding:14px 16px; box-shadow:0 2px 16px rgba(0,0,0,0.04); }
          .ep-prog-bar-bg { height:5px; background:#e2e8f0; border-radius:3px; overflow:hidden; margin-bottom:14px; }
          .ep-prog-bar-fill { height:100%; border-radius:3px; background:linear-gradient(90deg,#198754,#fd7e14); transition:width 0.6s cubic-bezier(0.4,0,0.2,1); }
          .ep-steps { display:flex; justify-content:center; gap:64px; max-width:480px; margin:0 auto; }
          .ep-step { display:flex; flex-direction:column; align-items:center; gap:4px; }
          .ep-step-dot { width:24px; height:24px; border-radius:50%; border:2px solid #e2e8f0; background:#fff; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; color:#cbd5e0; transition:all 0.3s; }
          .ep-step-dot.done { background:#198754; border-color:#198754; color:#fff; }
          .ep-step-dot.active { background:#fd7e14; border-color:#fd7e14; color:#fff; box-shadow:0 0 0 4px rgba(253,126,20,0.18); }
          .ep-step-lbl { font-size:10px; font-weight:600; color:#b0bec5; text-align:center; transition:color 0.3s; white-space:nowrap; }
          .ep-step-lbl.active { color:#fd7e14; }
          .ep-step-lbl.done  { color:#198754; }
          .ep-card { background:#fff; border-radius:22px; border:1px solid rgba(0,0,0,0.06); box-shadow:0 8px 32px rgba(0,0,0,0.06); max-width:620px; margin:32px auto 0; padding:40px 36px; animation:cardIn 0.38s cubic-bezier(0.4,0,0.2,1); }
          @keyframes cardIn { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:none} }
          .ep-recap-card { max-width:680px; padding:36px 32px; }
          .ep-formation-banner { background:linear-gradient(135deg,#1a2535 0%,#2d3f55 100%); border-radius:16px; padding:22px; margin-bottom:26px; color:#fff; }
          .ep-formation-title { font-size:15px; font-weight:700; line-height:1.5; margin-bottom:12px; }
          .ep-formation-meta { display:flex; flex-direction:column; gap:7px; }
          .ep-formation-meta-item { display:flex; align-items:center; gap:8px; font-size:13px; color:rgba(255,255,255,0.72); }
          .ep-formation-meta-item svg { color:#fd7e14; flex-shrink:0; }
          .ep-welcome-title { font-size:28px; font-weight:900; color:#1a202c; text-align:center; margin-bottom:10px; font-family:'DM Serif Display',serif; }
          .ep-welcome-sub { font-size:15px; color:#718096; text-align:center; line-height:1.7; margin-bottom:28px; }
          .ep-progress-pill { display:flex; align-items:center; gap:8px; background:#f0f7f4; border:1px solid #c6e8d5; border-radius:20px; padding:9px 18px; font-size:13px; font-weight:600; color:#718096; justify-content:center; margin-bottom:24px; }
          .ep-progress-pill span { color:#198754; font-weight:800; }
          .ep-anon-badge { display:flex; align-items:center; gap:9px; background:#f0f4ff; border:1px solid #c3cfe6; border-radius:12px; padding:10px 16px; font-size:13px; font-weight:600; color:#4a5568; margin-bottom:24px; }
          .ep-btn { width:100%; padding:16px; border:none; border-radius:13px; font-family:'DM Sans',sans-serif; font-size:15px; font-weight:700; cursor:pointer; transition:all 0.25s; display:flex; align-items:center; justify-content:center; gap:10px; }
          .ep-btn-primary { background:linear-gradient(135deg,#fd7e14 0%,#e65c00 100%); color:#fff; box-shadow:0 8px 24px rgba(253,126,20,0.28); }
          .ep-btn-primary:hover { transform:translateY(-2px); box-shadow:0 12px 32px rgba(253,126,20,0.4); }
          .ep-btn-primary:disabled { opacity:0.65; cursor:not-allowed; transform:none; }
          .ep-btn-secondary { background:#fff; color:#4a5568; border:1.5px solid #e2e8f0; }
          .ep-btn-secondary:hover { border-color:#fd7e14; color:#fd7e14; }
          .ep-nav-row { display:flex; gap:12px; margin-top:24px; }
          .ep-nav-row .ep-btn-secondary { width:auto; padding:16px 24px; flex-shrink:0; }
          .ep-step-title { font-size:22px; font-weight:800; color:#1a202c; margin-bottom:8px; font-family:'DM Serif Display',serif; }
          .ep-step-sub { font-size:14px; color:#718096; margin-bottom:24px; line-height:1.65; }
          .ep-textarea { width:100%; padding:14px 16px; border:1.5px solid #e2e8f0; border-radius:12px; font-family:'DM Sans',sans-serif; font-size:14px; color:#1a202c; outline:none; transition:border-color 0.2s,box-shadow 0.2s; background:#fafbfc; resize:vertical; min-height:80px; }
          .ep-textarea:focus { border-color:#fd7e14; background:#fff; box-shadow:0 0 0 3px rgba(253,126,20,0.1); }
          .ep-err { display:flex; align-items:center; gap:8px; padding:12px 16px; background:#fff5f5; border:1px solid #feb2b2; border-radius:10px; font-size:13.5px; font-weight:600; color:#dc3545; margin-top:12px; }
          .ep-section-block { margin-bottom:20px; border:1.5px solid #e2e8f0; border-radius:14px; overflow:hidden; }
          .ep-section-hd { display:flex; align-items:center; gap:10px; padding:13px 18px; font-size:12px; font-weight:800; text-transform:uppercase; letter-spacing:0.8px; border-bottom:1px solid #f0f4f8; border-left:4px solid; }
          .ep-critere-row { display:flex; align-items:center; justify-content:space-between; padding:12px 18px; border-bottom:1px solid #f5f7fa; gap:12px; transition:background 0.15s; }
          .ep-critere-row:last-child { border-bottom:none; }
          .ep-critere-row:hover { background:#fafbfc; }
          .ep-critere-label { font-size:13.5px; color:#4a5568; font-weight:500; flex:1; line-height:1.4; }
          .ep-stars { display:flex; gap:3px; flex-shrink:0; }
          .ep-star { font-size:24px; cursor:pointer; transition:transform 0.15s,color 0.1s; color:#e2e8f0; }
          .ep-star.on { color:#fd7e14; }
          .ep-star:hover { transform:scale(1.25); }
          .ep-reco-grid { display:flex; gap:10px; margin-bottom:20px; }
          .ep-reco-btn { flex:1; padding:13px 10px; border-radius:12px; border:1.5px solid #e2e8f0; background:#fafbfc; font-family:'DM Sans',sans-serif; font-size:13px; font-weight:600; color:#718096; cursor:pointer; transition:all 0.2s; display:flex; flex-direction:column; align-items:center; gap:6px; }
          .ep-reco-btn svg { font-size:18px; }
          .ep-reco-btn.sel-yes { background:#e6f4ee; border-color:#198754; color:#198754; }
          .ep-reco-btn.sel-maybe { background:#fff8ec; border-color:#fd7e14; color:#fd7e14; }
          .ep-reco-btn.sel-no { background:#fdf2f4; border-color:#dc3545; color:#dc3545; }
          .ep-free-section { margin-bottom:20px; }
          .ep-label { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:0.6px; color:#718096; margin-bottom:8px; display:block; }
          .ep-recap-header { text-align:center; margin-bottom:28px; }
          .ep-recap-icon { width:60px; height:60px; background:linear-gradient(135deg,#198754,#20c997); border-radius:18px; display:flex; align-items:center; justify-content:center; font-size:26px; color:#fff; margin:0 auto 16px; box-shadow:0 8px 24px rgba(25,135,84,0.25); }
          .ep-recap-global { display:flex; align-items:center; gap:24px; padding:22px; background:linear-gradient(135deg,#f0f7f4,#fff); border:1.5px solid #c6e8d5; border-radius:16px; margin-bottom:24px; }
          .ep-score-ring { width:96px; height:96px; border-radius:50%; flex-shrink:0; display:flex; align-items:center; justify-content:center; background:conic-gradient(var(--score-color) var(--score-deg), #e2e8f0 0deg); }
          .ep-score-inner { width:74px; height:74px; border-radius:50%; background:#fff; display:flex; flex-direction:column; align-items:center; justify-content:center; }
          .ep-score-num { font-size:26px; font-weight:900; color:#1a202c; line-height:1; }
          .ep-score-den { font-size:11px; color:#718096; font-weight:600; }
          .ep-score-label { font-size:18px; font-weight:800; margin-bottom:2px; }
          .ep-score-sub { font-size:12px; color:#718096; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:8px; }
          .ep-stars-row { display:flex; gap:2px; }
          .ep-recap-cats { display:flex; flex-direction:column; gap:10px; margin-bottom:28px; }
          .ep-recap-cat { display:grid; grid-template-columns:120px 1fr 36px; align-items:center; gap:12px; }
          .ep-recap-cat-label { font-size:13px; font-weight:600; color:#4a5568; }
          .ep-recap-cat-bar-wrap { height:8px; background:#e2e8f0; border-radius:4px; overflow:hidden; }
          .ep-recap-cat-bar { height:100%; border-radius:4px; transition:width 0.8s cubic-bezier(0.4,0,0.2,1); }
          .ep-recap-cat-score { font-size:13px; font-weight:800; text-align:right; }
          .ep-recap-section-title { font-size:12px; font-weight:800; text-transform:uppercase; letter-spacing:0.7px; color:#718096; margin-bottom:14px; display:flex; align-items:center; gap:8px; }
          .ep-recap-section-title::after { content:''; flex:1; height:1px; background:#e2e8f0; }
          .ep-done-icon { width:104px; height:104px; background:linear-gradient(135deg,#198754,#20c997); border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:46px; color:#fff; margin:0 auto 28px; animation:popIn 0.6s cubic-bezier(0.34,1.56,0.64,1); box-shadow:0 16px 48px rgba(25,135,84,0.38); }
          @keyframes popIn { from{opacity:0;transform:scale(0.4)} to{opacity:1;transform:none} }
          .ep-done-title { font-size:28px; font-weight:900; color:#1a202c; text-align:center; margin-bottom:12px; font-family:'DM Serif Display',serif; }
          .ep-done-sub { font-size:15px; color:#718096; text-align:center; line-height:1.75; margin-bottom:28px; max-width:440px; margin-left:auto; margin-right:auto; }
          .ep-done-badge { display:flex; align-items:center; gap:14px; background:linear-gradient(135deg,#1a2535,#2d3f55); border-radius:16px; padding:18px 22px; color:#fff; }
          .ep-done-badge-logo { width:46px; height:46px; background:#fd7e14; border-radius:12px; display:flex; align-items:center; justify-content:center; font-weight:900; font-size:14px; flex-shrink:0; letter-spacing:-0.5px; }
          .ep-done-badge-text { font-size:13px; color:rgba(255,255,255,0.7); }
          .ep-done-badge-name { font-size:15px; font-weight:700; color:#fff; margin-top:2px; }
          .ep-token-screen { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:60vh; gap:16px; text-align:center; padding:32px; }
          .ep-token-icon { font-size:48px; margin-bottom:8px; }
          .ep-token-title { font-size:20px; font-weight:700; color:#1a202c; }
          .ep-token-sub { font-size:14px; color:#718096; max-width:360px; line-height:1.6; }
          @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
          @media(max-width:640px) {
            .ep-card { margin:16px; padding:24px 18px; border-radius:16px; }
            .ep-recap-card { padding:22px 16px; }
            .ep-welcome-title { font-size:22px; }
            .ep-step-title { font-size:18px; }
            .ep-star { font-size:20px; }
            .ep-critere-label { font-size:12.5px; }
            .ep-lang { top:10px; right:10px; }
            .ep-recap-cat { grid-template-columns:90px 1fr 30px; }
            .ep-reco-grid { flex-direction:column; }
          }
        `}</style>

        {/* Toggle langue */}
        <div className="ep-lang">
          <button className={`ep-lang-btn ${lang === 'fr' ? 'on' : ''}`} onClick={() => setLang('fr')}>FR</button>
          <button className={`ep-lang-btn ${lang === 'en' ? 'on' : ''}`} onClick={() => setLang('en')}>EN</button>
        </div>

        {/*  CHARGEMENT  */}
        {tokenValid === null && (
          <div className="ep-token-screen">
            <FaSpinner style={{ fontSize: 40, color: '#198754', animation: 'spin 1s linear infinite' }} />
            <div className="ep-token-title">{t.loadingFormation}</div>
          </div>
        )}

        {/*  TOKEN INVALIDE / EXPIRÉ  */}
        {tokenValid === false && (
          <div className="ep-token-screen">
            <div className="ep-token-icon">⚠️</div>
            <div className="ep-token-title">{tokenError || t.tokenInvalid}</div>
            <div className="ep-token-sub">{tokenError || t.tokenInvalid}</div>
          </div>
        )}

        {/*  CONTENU PRINCIPAL  */}
        {tokenValid === true && (
          <>
            {step > 0 && (
              <div className="ep-prog-wrap">
                <div className="ep-prog-bar-bg">
                  <div className="ep-prog-bar-fill" style={{ width: `${progress}%` }} />
                </div>
                <div className="ep-steps">
                  {stepDots.map((sd, i) => {
                    const isDone   = step > sd.id;
                    const isActive = step === sd.id;
                    return (
                      <div key={sd.id} className="ep-step">
                        <div className={`ep-step-dot ${isDone ? 'done' : isActive ? 'active' : ''}`}>
                          {isDone ? '✓' : i + 1}
                        </div>
                        <div className={`ep-step-lbl ${isDone ? 'done' : isActive ? 'active' : ''}`}>{sd.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/*  ÉTAPE 0 — ACCUEIL  */}
            {step === 0 && (
              <div className="ep-card">
                <h1 className="ep-welcome-title">{t.welcome}</h1>
                <p className="ep-welcome-sub">{t.welcomeSub}</p>
                <div className="ep-formation-banner">
                  <div className="ep-formation-title">{formation.titre}</div>
                  <div className="ep-formation-meta">
                    <div className="ep-formation-meta-item"><FaCalendarAlt /> {formation.dates}</div>
                    <div className="ep-formation-meta-item"><FaMapMarkerAlt /> {formation.lieu}</div>
                    <div className="ep-formation-meta-item"><FaGraduationCap /> {formation.formateurs.join(', ')}</div>
                  </div>
                </div>
                <div className="ep-anon-badge">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style={{ color: '#0d6efd', flexShrink: 0 }}>
                    <path d="M12 2C9.243 2 7 4.243 7 7v3H6c-1.103 0-2 .897-2 2v8c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2v-8c0-1.103-.897-2-2-2h-1V7c0-2.757-2.243-5-5-5zm0 2c1.654 0 3 1.346 3 3v3H9V7c0-1.654 1.346-3 3-3zm1 10.723V17h-2v-2.277c-.595-.347-1-.985-1-1.723 0-1.103.897-2 2-2s2 .897 2 2c0 .738-.405 1.376-1 1.723z"/>
                  </svg>
                  {lang === 'fr'
                    ? 'Évaluation 100% anonyme — aucune donnée personnelle collectée'
                    : '100% anonymous — no personal data collected'}
                </div>
                <div className="ep-progress-pill">
                  <FaUsers style={{ color: '#fd7e14' }} />
                  <span>{formation.participantsRepondus}</span> / {formation.participantsAttendus}&nbsp;
                  {lang === 'fr' ? 'réponses reçues' : 'responses received'}
                </div>
                <button className="ep-btn ep-btn-primary" onClick={() => setStep(1)}>
                  {t.start} <FaChevronRight />
                </button>
              </div>
            )}

            {/*  ÉTAPE 1 — AVIS  */}
            {step === 1 && (
              <div className="ep-card">
                <h2 className="ep-step-title">{t.avisTitle}</h2>
                <p className="ep-step-sub">{t.avisRatingHint}</p>

                {SECTIONS_AVIS.map(section => (
                  <div key={section.key} className="ep-section-block">
                    <div className="ep-section-hd" style={{ color: section.color, borderLeftColor: section.color, background: `${section.color}09` }}>
                      {t[section.sectionKey]}
                    </div>
                    {section.criteres.map(critere => {
                      const noteKey = `${section.key}.${critere}`;
                      const val = notes[noteKey];
                      return (
                        <div key={critere} className="ep-critere-row">
                          <div className="ep-critere-label">{t.criteres[critere]}</div>
                          <div className="ep-stars">
                            {[1, 2, 3, 4, 5].map(star => (
                              <span key={star} className={`ep-star${val >= star ? ' on' : ''}`} onClick={() => setNote(noteKey, star)}>
                                {val >= star ? <FaStar /> : <FaRegStar />}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}

                <div className="ep-free-section">
                  <label className="ep-label">{t.recommandationLabel}</label>
                  <div className="ep-reco-grid">
                    {[
                      { val: 'yes',   cls: 'sel-yes',   icon: <FaThumbsUp />,  label: t.recommandationOpts[0] },
                      { val: 'maybe', cls: 'sel-maybe', icon: <FaMinus />,      label: t.recommandationOpts[1] },
                      { val: 'no',    cls: 'sel-no',    icon: <FaThumbsDown />, label: t.recommandationOpts[2] },
                    ].map(opt => (
                      <button key={opt.val}
                        className={`ep-reco-btn${recommandation === opt.val ? ' ' + opt.cls : ''}`}
                        onClick={() => setRecommandation(opt.val)}>
                        {opt.icon}<span>{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="ep-free-section">
                  <label className="ep-label">{t.pointsForts}</label>
                  <textarea className="ep-textarea" placeholder={t.pointsFortsPlaceholder} value={pointsForts} onChange={e => setPointsForts(e.target.value)} rows={3} />
                </div>
                <div className="ep-free-section">
                  <label className="ep-label">{t.pointsFaibles}</label>
                  <textarea className="ep-textarea" placeholder={t.pointsFaiblesPlaceholder} value={pointsFaibles} onChange={e => setPointsFaibles(e.target.value)} rows={3} />
                </div>
                <div className="ep-free-section">
                  <label className="ep-label">{t.commentaireLabel}</label>
                  <textarea className="ep-textarea" placeholder={t.commentairePlaceholder} value={commentaire} onChange={e => setCommentaire(e.target.value)} rows={3} />
                </div>

                {avisErr && <div className="ep-err"><FaExclamationTriangle /> {avisErr}</div>}

                <div className="ep-nav-row">
                  <button className="ep-btn ep-btn-secondary" onClick={() => setStep(0)}><FaChevronLeft /> {t.prev}</button>
                  <button className="ep-btn ep-btn-primary" onClick={handleSubmitAvis}>{t.next} <FaChevronRight /></button>
                </div>
              </div>
            )}

            {/*  ÉTAPE 2 — RÉCAP  */}
            {step === 2 && (
              <>
                {submitErr && (
                  <div className="ep-card" style={{ marginBottom: 0 }}>
                    <div className="ep-err"><FaExclamationTriangle /> {submitErr}</div>
                  </div>
                )}
                <RecapitulatifAvis notes={notes} t={t} lang={lang} onContinue={handleFinalSubmit} submitting={submitting} />
              </>
            )}

            {/*  ÉTAPE 3 — CONFIRMATION  */}
            {step === 3 && (
              <div className="ep-card" style={{ textAlign: 'center' }}>
                <div className="ep-done-icon"><FaAward /></div>
                <h2 className="ep-done-title">{t.doneTitle}</h2>
                <p className="ep-done-sub">{t.doneSub}</p>
                <div className="ep-done-badge">
                  <div className="ep-done-badge-logo">CINEF</div>
                  <div>
                    <div className="ep-done-badge-name">{t.doneSign}</div>
                    <div className="ep-done-badge-text">cinef-bj.com · info@cinef-bj.com</div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default EvalPublic;